import React from 'react';
import { EventResultsSettingsState, SelectionsGroupType } from './EventResultsSettingsState';
import { EventResultsSettingsSelection } from './EventResultsSettingsSelection';
import { EventResultsState } from 'src/domains/sportsbook/trading/view/eventDetails/eventResults/EventResults.state';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';

const renderDisabledButton = (label: string): React.ReactElement => {
    return (
        <a
            href='#'
            className='btn disabled'
        >
            {label}
        </a>
    );
};

interface CollectionButtonPropsType {
    label: string;
    hide?: boolean;
    collection: SelectionsGroupType;
}

export const CollectionButtonAction = observer(
    'CollectionButton',
    (props: CollectionButtonPropsType): React.ReactElement | null => {
        const { label, hide = false, collection } = props;

        if (collection.type === 'hide') {
            if (hide) {
                return null;
            } else {
                return renderDisabledButton(label);
            }
        }

        if (collection.type === 'disable') {
            return renderDisabledButton(label);
        }

        return (
            <a
                href='#'
                className='btn'
                onClick={collection.onClick}
            >
                {label}
            </a>
        );
    }
);

interface PropsType {
    eventId: EventId;
    marketId: MarketId;
    eventResultsState: EventResultsState;
}

export const EventResultsSettings = observer('EventResultsSettings', (props: PropsType) => {
    const appState = useAppStateContext();

    const [settingsState] = React.useState(
        () => new EventResultsSettingsState(appState, props.eventResultsState, props.eventId, props.marketId)
    );

    const isPlace = settingsState.isPlace;

    return (
        <div className='event-results-settings'>
            <table className='form'>
                <thead>
                    <tr>
                        <th />
                        <th>Result</th>
                        {isPlace ? <th>Place</th> : null}
                        {settingsState.isSp ? <th>SP</th> : null}
                        <th>Win reduction</th>
                        {settingsState.eachWay.length > 0 ? (
                            <th colSpan={settingsState.eachWay.length}>Reductions</th>
                        ) : null}
                        <th>Lock</th>
                        <th>Settle selection</th>
                    </tr>
                    <tr>
                        <th colSpan={2}>
                            <CollectionButtonAction
                                collection={settingsState.selectionsForSetEmptyAsVoid}
                                label='Void the rest'
                            />
                            <CollectionButtonAction
                                collection={settingsState.selectionsForSetEmptyAsLost}
                                label='Set rest to lose'
                            />
                        </th>
                        {isPlace ? <th /> : null}
                        {settingsState.isSp ? <th /> : null}
                        <th />
                        {settingsState.eachWay.map((item) => {
                            const places = item.places.toString();
                            const reduction = item.reduction;
                            const key = `${places}-${reduction}`;
                            const label = `${places} @ ${reduction}`;

                            return <th key={key}>{label}</th>;
                        })}
                        <th>
                            <CollectionButtonAction
                                collection={settingsState.selectionsForUnlock}
                                label='Select all'
                            />
                        </th>
                        <th>
                            <CollectionButtonAction
                                collection={settingsState.selectionsForSave}
                                label='Save all'
                            />

                            <CollectionButtonAction
                                collection={settingsState.selectionsForSettle}
                                label='Settle all'
                            />

                            <CollectionButtonAction
                                collection={settingsState.selectionsForUnsettle}
                                label='Unsettle all'
                                hide={true}
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {settingsState.selections.map((selectionState) => (
                        <EventResultsSettingsSelection
                            key={selectionState.selectionId.key}
                            selectionState={selectionState}
                            settingsState={settingsState}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
});
